import { useEffect, useState, useRef, useCallback } from "react";

const DEFAULT_TIMEOUT = 30000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchmove"];

/**
 * React hook that detects user inactivity.
 * @param {number} timeout amount of time in milliseconds until the user is considered inactive
 * @returns boolean indicating whether the user is inactive
 */
export function useInactivity(timeout = DEFAULT_TIMEOUT) {
  const [isInactivity, setIsInactivity] = useState(false);
  const inactivityRef = useRef(false);
  const timerRef = useRef(null);

  const onInactivity = useCallback(() => {
    if (!inactivityRef.current) {
      inactivityRef.current = true;
      setIsInactivity(true);
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (inactivityRef.current) {
      inactivityRef.current = false;
      setIsInactivity(false);
    }

    timerRef.current = setTimeout(onInactivity, timeout);
  }, [timeout, onInactivity]);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer)
    );

    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resetTimer]);

  return [isInactivity, resetTimer];
}
